/**
 * DIN 276 Kostengruppe → icon + color mapping (single source for asset colour-coding).
 *
 * Icons are Material Symbols names (https://fonts.google.com/icons); colours are a coherent
 * per-Kostengruppe palette. The mapping is defined per group; any specific code inherits the
 * nearest group via {@link resolveDin276Style} (e.g. 344.11 → 344 → 340 → 300 → default).
 *
 * These values are also persisted onto the din276 Taxonomy nodes (fields `icon`/`color`) so the
 * mapping lives "with the metadata" in the DB; the read path prefers a node's stored value and
 * falls back to this resolver, so colours work even for nodes not yet migrated.
 */

export interface Din276Style {
  icon: string;
  color: string;
}

/**
 * Keyed by DIN 276 group code (hundreds, tens, and a few unit groups). Construction (300s) keeps
 * muted structural tones; the technical trades (400s) use vivid, semantically-loaded colours —
 * water = strong blue, heat/fire = strong red-orange, air = cyan, electrical = amber, etc.
 */
const DIN276_STYLES: Record<string, Din276Style> = {
  // 100 Grundstück
  '100': { icon: 'landscape', color: '#00C853' },
  // 200 Vorbereitende Maßnahmen
  '200': { icon: 'engineering', color: '#FF6D00' },
  // 300 Bauwerk – Baukonstruktionen (muted structural tones)
  '300': { icon: 'foundation', color: '#546E7A' },
  '310': { icon: 'terrain', color: '#795548' }, // Baugrube / Erdbau
  '320': { icon: 'foundation', color: '#5D4037' }, // Gründung, Unterbau
  '330': { icon: 'square', color: '#607D8B' }, // Außenwände / vertikale Baukonstruktionen außen
  '334': { icon: 'sensor_door', color: '#3D5AFE' }, // Außentüren und -fenster
  '340': { icon: 'grid_view', color: '#78909C' }, // Innenwände / vertikale Baukonstruktionen innen
  '344': { icon: 'door_front', color: '#7C4DFF' }, // Innentüren und -fenster
  '350': { icon: 'table_rows', color: '#90A4AE' }, // Decken / horizontale Baukonstruktionen
  '360': { icon: 'roofing', color: '#455A64' }, // Dächer
  '370': { icon: 'shelves', color: '#8D6E63' }, // Baukonstruktive Einbauten
  '390': { icon: 'category', color: '#B0BEC5' }, // Sonstige Baukonstruktionen
  // 400 Bauwerk – Technische Anlagen (vivid, semantic)
  '400': { icon: 'settings', color: '#37474F' },
  '410': { icon: 'water_drop', color: '#0091EA' }, // Abwasser, Wasser, Gas — water blue
  '420': { icon: 'mode_heat', color: '#FF3D00' }, // Wärmeversorgung — fire red-orange
  '430': { icon: 'air', color: '#00B8D4' }, // Raumlufttechnik — air cyan
  '440': { icon: 'bolt', color: '#FFC400' }, // Elektrische Anlagen — electric amber
  '450': { icon: 'sensors', color: '#AA00FF' }, // Kommunikation/Sicherheit — purple
  '460': { icon: 'elevator', color: '#FF6F00' }, // Förderanlagen — dark orange
  '470': { icon: 'precision_manufacturing', color: '#00BFA5' }, // Nutzungsspezifisch/verfahrenstechnisch
  '471': { icon: 'kitchen', color: '#00BFA5' }, // Küchentechnische Anlagen
  '472': { icon: 'local_laundry_service', color: '#00BFA5' }, // Wäscherei/Reinigung/Bad
  '473': { icon: 'science', color: '#00BFA5' }, // Medienversorgung/Medizin-/Labortechnik
  '473.10': { icon: 'air', color: '#00BFA5' }, // Druckluftversorgungsanlagen
  '473.20': { icon: 'gas_meter', color: '#00BFA5' }, // Versorgung für technische Gase
  '473.30': { icon: 'storage', color: '#00BFA5' }, // Tankläger
  '473.40': { icon: 'bathtub', color: '#00BFA5' }, // Medizinische Wanneneinrichtungen
  '473.50': { icon: 'sync', color: '#00BFA5' }, // Zentrifugen
  '473.70': { icon: 'shower', color: '#00BFA5' }, // Sicherheitsnotduschen
  '479.20': { icon: 'medical_services', color: '#00BFA5' }, // Röntgengeräte
  '474': { icon: 'local_fire_department', color: '#D50000' }, // Feuerlöschanlagen — fire red
  '474.7': { icon: 'fire_extinguisher', color: '#D50000' }, // Feuerlöscher (portable) — fire red
  '475': { icon: 'mode_fan', color: '#00BFA5' }, // Prozesswärme/-kälte/-luft
  '476': { icon: 'precision_manufacturing', color: '#00BFA5' }, // Weitere nutzungsspezifische
  '477': { icon: 'plumbing', color: '#00BFA5' }, // Verfahrenstechnik Wasser/Abwasser/Gase
  '478': { icon: 'recycling', color: '#00BFA5' }, // Verfahrenstechnik Feststoffe/Wertstoffe/Abfälle
  '479': { icon: 'category', color: '#00BFA5' }, // Sonstiges zur KG 470
  '480': { icon: 'memory', color: '#304FFE' }, // Gebäude- und Anlagenautomation — indigo
  '490': { icon: 'category', color: '#90A4AE' }, // Sonstige technische Anlagen
  // 500 Außenanlagen und Freiflächen
  '500': { icon: 'park', color: '#64DD17' },
  // 600 Ausstattung und Kunstwerke
  '600': { icon: 'chair', color: '#00ACC1' },
  // 700 Baunebenkosten
  '700': { icon: 'receipt_long', color: '#757575' },
};

/** Fallback when a code matches no group (e.g. non-numeric composition-type codes). */
export const DEFAULT_DIN276_STYLE: Din276Style = { icon: 'category', color: '#90A4AE' };

/**
 * Resolve the icon + color for a DIN 276 code, inheriting from the nearest group:
 * exact 3-digit group → tens group (x·10) → hundreds group (x·100) → default.
 */
export const resolveDin276Style = (code: string | undefined | null): Din276Style => {
  const raw = String(code ?? '').trim();
  // Exact code override first (e.g. a specific leaf mapped by full code).
  if (raw && DIN276_STYLES[raw]) {
    return DIN276_STYLES[raw];
  }
  const m = raw.match(/(\d{3})(?:\.(\d))?/);
  if (m) {
    const threeDigit = m[1]; // 474
    const firstDecimal = m[2]; // 7 (of 474.70) — enables a sub-group override like "474.7"
    if (firstDecimal && DIN276_STYLES[`${threeDigit}.${firstDecimal}`]) {
      return DIN276_STYLES[`${threeDigit}.${firstDecimal}`];
    }
    if (DIN276_STYLES[threeDigit]) {
      return DIN276_STYLES[threeDigit];
    }
    const tensGroup = `${threeDigit[0]}${threeDigit[1]}0`; // 421 → 420
    if (DIN276_STYLES[tensGroup]) {
      return DIN276_STYLES[tensGroup];
    }
    const hundredsGroup = `${threeDigit[0]}00`; // 421 → 400
    if (DIN276_STYLES[hundredsGroup]) {
      return DIN276_STYLES[hundredsGroup];
    }
  }
  return DEFAULT_DIN276_STYLE;
};

export { DIN276_STYLES };
